/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { CodedError } from '@karya/error-values';

/**
 * Centralized error classes for user components
 * Provides consistent, reusable error classes across all components
 */

// ============================================================================
// Media Recording Errors
// ============================================================================

type MediaDeviceType = 'camera' | 'microphone' | 'both';

type PermissionDeniedErrorDetails = {
  deviceType: MediaDeviceType;
};

type DeviceNotFoundErrorDetails = {
  deviceType: MediaDeviceType;
};

type DeviceInUseErrorDetails = {
  deviceType: MediaDeviceType;
};

type DeviceConstraintErrorDetails = {
  deviceType: MediaDeviceType;
};

type RecordingFailedErrorDetails = {
  deviceType: MediaDeviceType;
};

type RecordingErrorDetails = Record<string, never>;

type MinRecordingLengthErrorDetails = {
  minLength: number;
  currentLength: number;
};

type MaxRecordingLengthErrorDetails = {
  maxLength: number;
  currentLength: number;
};

type MediaDeviceErrorDetails = {
  deviceType: MediaDeviceType;
  originalError?: unknown;
};

export class PermissionDeniedError extends CodedError<'PermissionDeniedError', PermissionDeniedErrorDetails> {
  constructor(deviceType: MediaDeviceType = 'both') {
    const device = deviceType === 'both' ? 'Camera and microphone' : deviceType === 'camera' ? 'Camera' : 'Microphone';
    super('PermissionDeniedError', `${device} access was denied. Please allow access and try again.`, { deviceType });
  }
}

export class DeviceNotFoundError extends CodedError<'DeviceNotFoundError', DeviceNotFoundErrorDetails> {
  constructor(deviceType: MediaDeviceType = 'both') {
    const device = deviceType === 'both' ? 'camera or microphone' : deviceType === 'camera' ? 'camera' : 'microphone';
    super('DeviceNotFoundError', `No ${device} found. Please connect a device and try again.`, { deviceType });
  }
}

export class DeviceInUseError extends CodedError<'DeviceInUseError', DeviceInUseErrorDetails> {
  constructor(deviceType: MediaDeviceType = 'both') {
    const device = deviceType === 'both' ? 'Camera or microphone' : deviceType === 'camera' ? 'Camera' : 'Microphone';
    super(
      'DeviceInUseError',
      `${device} is already in use by another application. Please close other applications and try again.`,
      {
        deviceType,
      },
    );
  }
}

export class DeviceConstraintError extends CodedError<'DeviceConstraintError', DeviceConstraintErrorDetails> {
  constructor(deviceType: MediaDeviceType = 'both') {
    const device = deviceType === 'both' ? 'Camera or microphone' : deviceType === 'camera' ? 'Camera' : 'Microphone';
    super('DeviceConstraintError', `${device} does not support the required settings. Please try a different device.`, {
      deviceType,
    });
  }
}

export class RecordingFailedError extends CodedError<'RecordingFailedError', RecordingFailedErrorDetails> {
  constructor(deviceType: MediaDeviceType = 'both') {
    const device = deviceType === 'both' ? 'camera and microphone' : deviceType === 'camera' ? 'camera' : 'microphone';
    super('RecordingFailedError', `Failed to start recording. Please check your ${device} permissions and try again.`, {
      deviceType,
    });
  }
}

export class RecordingError extends CodedError<'RecordingError', RecordingErrorDetails> {
  constructor() {
    super('RecordingError', 'An error occurred while recording. Please try again.', {});
  }
}

export class MinRecordingLengthError extends CodedError<'MinRecordingLengthError', MinRecordingLengthErrorDetails> {
  constructor(minLength: number, currentLength: number) {
    super(
      'MinRecordingLengthError',
      `Recording must be at least ${minLength} seconds long. Current: ${currentLength}s`,
      { minLength, currentLength },
    );
  }
}

export class MaxRecordingLengthError extends CodedError<'MaxRecordingLengthError', MaxRecordingLengthErrorDetails> {
  constructor(maxLength: number, currentLength: number) {
    super('MaxRecordingLengthError', `Recording must not exceed ${maxLength} seconds. Current: ${currentLength}s`, {
      maxLength,
      currentLength,
    });
  }
}

export class MediaDeviceError extends CodedError<'MediaDeviceError', MediaDeviceErrorDetails> {
  constructor(error: unknown, deviceType: MediaDeviceType = 'both') {
    let message: string;
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        message = new PermissionDeniedError(deviceType).message;
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        message = new DeviceNotFoundError(deviceType).message;
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        message = new DeviceInUseError(deviceType).message;
      } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
        message = new DeviceConstraintError(deviceType).message;
      } else {
        message = new RecordingFailedError(deviceType).message;
      }
    } else {
      message = new RecordingFailedError(deviceType).message;
    }
    super('MediaDeviceError', message, { deviceType, originalError: error });
  }
}

// ============================================================================
// File Validation Errors
// ============================================================================

type InvalidFileTypeErrorDetails = {
  expectedType: string;
};

type FileSizeExceededErrorDetails = {
  fileSizeMB: number;
  maxSizeMB: number;
};

type InvalidImageErrorDetails = Record<string, never>;

export class InvalidFileTypeError extends CodedError<'InvalidFileTypeError', InvalidFileTypeErrorDetails> {
  constructor(expectedType: string = 'file') {
    super('InvalidFileTypeError', `Please select a valid ${expectedType} file.`, { expectedType });
  }
}

export class FileSizeExceededError extends CodedError<'FileSizeExceededError', FileSizeExceededErrorDetails> {
  constructor(fileSizeMB: number, maxSizeMB: number) {
    super('FileSizeExceededError', `File is too large (${fileSizeMB.toFixed(2)}MB). Maximum size is ${maxSizeMB}MB.`, {
      fileSizeMB,
      maxSizeMB,
    });
  }
}

export class InvalidImageError extends CodedError<'InvalidImageError', InvalidImageErrorDetails> {
  constructor() {
    super('InvalidImageError', 'Invalid image file. Please select a valid image.', {});
  }
}

// ============================================================================
// Text Validation Errors
// ============================================================================

type EmptyInputErrorDetails = {
  minLength?: number;
};

type MinLengthErrorDetails = {
  minLength: number;
};

type MaxLengthErrorDetails = {
  maxLength: number;
};

type InvalidNumberErrorDetails = {
  type: 'integer' | 'number';
};

type InvalidIntegerErrorDetails = Record<string, never>;

type MinValueErrorDetails = {
  min: number;
};

type MaxValueErrorDetails = {
  max: number;
};

export class EmptyInputError extends CodedError<'EmptyInputError', EmptyInputErrorDetails> {
  constructor(minLength?: number) {
    const message =
      minLength !== undefined && minLength > 0
        ? `Please enter at least ${minLength} character${minLength !== 1 ? 's' : ''}`
        : 'Please enter a value';
    super('EmptyInputError', message, { minLength });
  }
}

export class MinLengthError extends CodedError<'MinLengthError', MinLengthErrorDetails> {
  constructor(minLength: number) {
    super('MinLengthError', `Minimum length is ${minLength} characters`, { minLength });
  }
}

export class MaxLengthError extends CodedError<'MaxLengthError', MaxLengthErrorDetails> {
  constructor(maxLength: number) {
    super('MaxLengthError', `Maximum length is ${maxLength} characters`, { maxLength });
  }
}

export class InvalidNumberError extends CodedError<'InvalidNumberError', InvalidNumberErrorDetails> {
  constructor(type: 'integer' | 'number') {
    super('InvalidNumberError', `Please enter a valid ${type}`, { type });
  }
}

export class InvalidIntegerError extends CodedError<'InvalidIntegerError', InvalidIntegerErrorDetails> {
  constructor() {
    super('InvalidIntegerError', 'Please enter a valid integer', {});
  }
}

export class MinValueError extends CodedError<'MinValueError', MinValueErrorDetails> {
  constructor(min: number) {
    super('MinValueError', `Minimum value is ${min}`, { min });
  }
}

export class MaxValueError extends CodedError<'MaxValueError', MaxValueErrorDetails> {
  constructor(max: number) {
    super('MaxValueError', `Maximum value is ${max}`, { max });
  }
}

// ============================================================================
// MCQ Validation Errors
// ============================================================================

type NoOptionsErrorDetails = Record<string, never>;

type MinSelectionErrorDetails = {
  min: number;
};

type MaxSelectionErrorDetails = {
  max: number;
};

type OtherOptionRequiredErrorDetails = Record<string, never>;

export class NoOptionsError extends CodedError<'NoOptionsError', NoOptionsErrorDetails> {
  constructor() {
    super('NoOptionsError', 'No options available. Please contact support.', {});
  }
}

export class MinSelectionError extends CodedError<'MinSelectionError', MinSelectionErrorDetails> {
  constructor(min: number) {
    super('MinSelectionError', `Please select at least ${min} option${min !== 1 ? 's' : ''}`, { min });
  }
}

export class MaxSelectionError extends CodedError<'MaxSelectionError', MaxSelectionErrorDetails> {
  constructor(max: number) {
    super('MaxSelectionError', `Please select at most ${max} option${max !== 1 ? 's' : ''}`, { max });
  }
}

export class OtherOptionRequiredError extends CodedError<'OtherOptionRequiredError', OtherOptionRequiredErrorDetails> {
  constructor() {
    super('OtherOptionRequiredError', 'Please specify a value for "Other" option', {});
  }
}

// ============================================================================
// Generic Errors
// ============================================================================

type SubmissionFailedErrorDetails = Record<string, never>;

type InvalidInputErrorDetails = Record<string, never>;

export class SubmissionFailedError extends CodedError<'SubmissionFailedError', SubmissionFailedErrorDetails> {
  constructor() {
    super('SubmissionFailedError', 'Failed to submit. Please try again.', {});
  }
}

export class InvalidInputError extends CodedError<'InvalidInputError', InvalidInputErrorDetails> {
  constructor() {
    super('InvalidInputError', 'Invalid input. Please check your entry and try again.', {});
  }
}
